import CloseIcon from "@mui/icons-material/Close"
import InventoryIcon from "@mui/icons-material/Inventory"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import PersonIcon from "@mui/icons-material/Person"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography
} from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { useMemo } from "react"
import {
  SanitizedMarketplaceOrder,
  SanitizedMarketplaceOrdersResponse
} from "./MarketplaceOrderList"
import { formatDate } from "@utils/functions/formatDate"

interface Props {
  open: boolean
  onClose: () => void
  orderId: string | null
}

const getStatusColor = (
  status: string | null | undefined
): "success" | "warning" | "error" | "default" | "info" => {
  if (!status) return "default"
  const s = status.toLowerCase()
  if (["completed", "delivered", "success", "paid"].includes(s))
    return "success"
  if (["cancelled", "failed", "returned", "refunded"].includes(s))
    return "error"
  if (["pending", "processing", "shipped", "awaiting_payment"].includes(s))
    return "warning"
  return "info"
}

export default function MarketplaceOrderVisualizerDrawer({
  open,
  onClose,
  orderId
}: Props) {
  const queryClient = useQueryClient()

  const order = useMemo<SanitizedMarketplaceOrder | null>(() => {
    if (!orderId) return null
    const queriesData =
      queryClient.getQueriesData<SanitizedMarketplaceOrdersResponse>({
        queryKey: ["marketplace-orders-list"]
      })

    for (const [, data] of queriesData) {
      if (data && data.orders) {
        const found = data.orders.find((o) => o.orderId === orderId)
        if (found) return found
      }
    }
    return null
  }, [orderId, queryClient])

  const renderSectionHeader = (icon: React.ReactNode, title: string) => (
    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
      <Avatar
        sx={{
          bgcolor: "primary.light",
          color: "primary.main",
          width: 32,
          height: 32
        }}
      >
        {icon}
      </Avatar>
      <Typography variant="subtitle1" fontWeight="600">
        {title}
      </Typography>
    </Stack>
  )

  const renderInfoRow = (label: string, value: string | React.ReactNode) => (
    <Box mb={1.5}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {typeof value === "string" ? (
        <Typography variant="body2" fontWeight="500">
          {value || "-"}
        </Typography>
      ) : (
        value || <Typography variant="body2">-</Typography>
      )}
    </Box>
  )

  const truncateText = (text: string, maxLength = 26) => {
    if (!text || text.length <= maxLength) return text

    return text.slice(0, maxLength) + "..."
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "80%", md: "60%", lg: 600 },
          bgcolor: "background.default"
        }
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
            position: "sticky",
            top: 0,
            zIndex: 1
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h5" fontWeight="700" gutterBottom>
                Order Details
              </Typography>
              {order ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={order.orderId}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip label={order.seller} size="small" color="secondary" />
                  <Chip
                    label={order.type}
                    size="small"
                    sx={{ textTransform: "capitalize" }}
                  />
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading order details...
                </Typography>
              )}
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ bgcolor: "action.hover" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {/* Content */}
        {order && (
          <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
            <Grid container spacing={3}>
              {/* Main Statuses */}
              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
                >
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={4}>
                        {renderInfoRow(
                          "Total Amount",
                          <Typography
                            variant="h6"
                            color="primary.main"
                            fontWeight="600"
                          >
                            {order.amountPaid} {order.currency}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        {renderInfoRow(
                          "Order Status",
                          <Chip
                            label={order.orderStatus}
                            size="small"
                            color={getStatusColor(order.orderStatus)}
                          />
                        )}
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        {renderInfoRow(
                          "Created At",
                          formatDate("en-US", order.createdAt.toString())
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Advanced Statuses */}
              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
                >
                  <CardContent>
                    {renderSectionHeader(
                      <ReceiptLongIcon fontSize="small" />,
                      "Statuses"
                    )}
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        {renderInfoRow(
                          "Payment",
                          <Chip
                            label={order.paymentStatus || "Unknown"}
                            size="small"
                            variant="outlined"
                            color={getStatusColor(order.paymentStatus)}
                          />
                        )}
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        {renderInfoRow(
                          "Checkout",
                          <Chip
                            label={order.checkoutStatus || "Unknown"}
                            size="small"
                            variant="outlined"
                            color={getStatusColor(order.checkoutStatus)}
                          />
                        )}
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        {renderInfoRow(
                          "Return",
                          <Chip
                            label={order.returnStatus || "None"}
                            size="small"
                            variant="outlined"
                            color={getStatusColor(order.returnStatus)}
                          />
                        )}
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        {renderInfoRow(
                          "Refund",
                          <Chip
                            label={order.refundStatus || "None"}
                            size="small"
                            variant="outlined"
                            color={getStatusColor(order.refundStatus)}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Buyer & Shipping Area */}
              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    height: "100%"
                  }}
                >
                  <CardContent>
                    {renderSectionHeader(
                      <PersonIcon fontSize="small" />,
                      "Buyer Info"
                    )}
                    <Stack spacing={1.5}>
                      {renderInfoRow(
                        "Name",
                        order.buyer?.firstName + " " + order.buyer?.lastName
                      )}
                      {renderInfoRow(
                        "Email",
                        truncateText(order.buyer?.email || "")
                      )}
                      {renderInfoRow("Username", order.buyer?.userId)}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    height: "100%"
                  }}
                >
                  <CardContent>
                    {renderSectionHeader(
                      <LocalShippingIcon fontSize="small" />,
                      "Shipping Details"
                    )}
                    {order.shippingAddress ? (
                      <Stack spacing={1.5}>
                        {renderInfoRow("Recipient", order.shippingAddress.name)}
                        {renderInfoRow(
                          "Address",
                          `${order.shippingAddress.street1} ${order.shippingAddress.street2 || ""}
                          ${order.shippingAddress.city}, ${order.shippingAddress.stateOrProvince || ""}
                          ${order.shippingAddress.postalCode} ${order.shippingAddress.country || ""}`
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No shipping address provided.
                      </Typography>
                    )}

                    {order.shippingService && (
                      <Box mt={2}>
                        <Divider sx={{ mb: 2 }} />
                        {renderInfoRow(
                          "Service Name",
                          order.shippingService.name
                        )}
                        {renderInfoRow(
                          "Cost",
                          `${order.shippingService.cost} ${order.currency}`
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Items Detail */}
              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
                >
                  <CardContent sx={{ pb: "8px !important" }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                    >
                      {renderSectionHeader(
                        <InventoryIcon fontSize="small" />,
                        "Order Items"
                      )}
                      <Chip label={`${order.itemsLength} items`} size="small" />
                    </Stack>

                    <List disablePadding>
                      {order.items?.map((item, idx) => (
                        <Box key={item.id || idx}>
                          <ListItem
                            alignItems="flex-start"
                            sx={{ px: 0, py: 1.5 }}
                          >
                            <ListItemAvatar>
                              <Avatar
                                variant="rounded"
                                src={""}
                                alt={item.title}
                                sx={{
                                  width: 56,
                                  height: 56,
                                  bgcolor: "grey.200"
                                }}
                              >
                                {<InventoryIcon color="action" />}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              sx={{ ml: 2, my: 0 }}
                              primary={
                                <Typography
                                  variant="subtitle2"
                                  fontWeight="600"
                                  gutterBottom
                                >
                                  {item.title || "Unknown Item"}
                                </Typography>
                              }
                              secondary={
                                <Stack
                                  direction="row"
                                  spacing={2}
                                  sx={{ mt: 0.5 }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Quantity
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight="500"
                                    >
                                      x{item.quantity}
                                    </Typography>
                                  </Box>
                                  {item.sku && (
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                      >
                                        SKU
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        fontWeight="500"
                                      >
                                        {item.sku}
                                      </Typography>
                                    </Box>
                                  )}
                                </Stack>
                              }
                            />
                          </ListItem>
                          {idx < order.items.length - 1 && (
                            <Divider component="li" />
                          )}
                        </Box>
                      ))}
                      {(!order.items || order.items.length === 0) && (
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText primary="No items found for this order." />
                        </ListItem>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </Drawer>
  )
}
